import { createRepairReport } from '#/api/aftersalesMgmt/repairReport/create';
import { fetchRepairReportLatestBySerialNo } from '#/api/aftersalesMgmt/repairReport/fetchLatestBySerialNo';
import { fetchRepairReportNextNo } from '#/api/aftersalesMgmt/repairReport/fetchNextNo';
import { fetchRepairItemHistoryBySerialNo } from '#/api/aftersalesMgmt/repairItem/fetchHistoryBySerialNo';
import {
  getExistingRepairReportIdFromError,
  getRepairReportApiErrorMessage,
  isRepairReportAlreadyExists,
  isRepairReportNoDuplicated,
} from '#/modules/crmMgmt/aftersales/repairReport/helper/apiError';
import { resolveRepairItemIdForReportLine } from '#/modules/crmMgmt/aftersales/repairRequest/helper/repairLineItemAccess';
import { toast } from '#/utils/toast';

/**
 * 報修明細列 → 既有維修報告 id（有 serialNo 查 latest；無則 null）。
 * 不 throw；404／無報告回 null。
 *
 * @param {object} line
 * @returns {Promise<string|null>}
 */
export async function resolveRepairReportIdForLine(line) {
  const serialNo = String(line?.serialNo ?? '').trim();
  if (!serialNo) {
    return null;
  }

  try {
    const latest = await fetchRepairReportLatestBySerialNo(serialNo);
    return latest?.data?.report?.id ?? null;
  } catch {
    return null;
  }
}

/**
 * 從 409 ALREADY_EXISTS／SN 歷史／latest 解析既有報告 id。
 */
export async function resolveExistingRepairReportId({
  error,
  serialNo = null,
  repairItemId = null,
} = {}) {
  const fromDetails = getExistingRepairReportIdFromError(error);
  if (fromDetails) {
    return fromDetails;
  }

  const sn = String(serialNo ?? '').trim();
  if (sn) {
    try {
      const history = await fetchRepairItemHistoryBySerialNo({
        serialNo: sn,
        page: 1,
        pageSize: 50,
      });
      const rows = history?.data ?? [];
      const matched = rows.find((entry) => {
        const itemId = entry?.repairItem?.id;
        if (repairItemId && itemId && itemId !== repairItemId) {
          return false;
        }
        return Boolean(entry?.report?.id);
      });
      if (matched?.report?.id) {
        return matched.report.id;
      }
    } catch {
      // fall through
    }

    try {
      const latest = await fetchRepairReportLatestBySerialNo(sn);
      return latest?.data?.report?.id ?? null;
    } catch {
      return null;
    }
  }

  return null;
}

async function fetchSuggestedRepairReportNo() {
  const nextNoResult = await fetchRepairReportNextNo();
  const repairReportNo = typeof nextNoResult?.data === 'string'
    ? nextNoResult.data
    : '';
  if (!repairReportNo) {
    throw new Error('repair report no missing');
  }
  return repairReportNo;
}

/**
 * 報修明細一鍵：有 SN 先查 latest；已有則不 create。
 * 否則 next-no → create（僅 repairItemId＋repairReportNo）。
 * - 成功：回 `{ created: true, data }`
 * - 已存在（pre-check／409）：回 `{ alreadyExists: true, existingId }`
 * - 409 NO_DUPLICATED：重取 next-no、toast，回 `{ noDuplicated: true }`（**不**自動重送）
 *
 * @returns {Promise<object>}
 */
export async function createRepairReportFromRepairLine(line, {
  repairRequestNo = null,
} = {}) {
  const serialNo = String(line?.serialNo ?? '').trim();
  if (serialNo) {
    const existingId = await resolveRepairReportIdForLine(line);
    if (existingId) {
      toast.info('此序號已有維修報告');
      return { alreadyExists: true, existingId };
    }
  }

  const repairItemId = await resolveRepairItemIdForReportLine(line, {
    repairRequestNo,
  });
  if (!repairItemId) {
    toast.error(
      '找不到對應維修品項（報修 detail 未回 repair_item_id，且 SN 歷史／search 無匹配）',
    );
    throw new Error('缺少維修品項 id');
  }

  let repairReportNo;
  try {
    repairReportNo = await fetchSuggestedRepairReportNo();
  } catch (error) {
    toast.error(getRepairReportApiErrorMessage(error, '取得建議單號失敗'));
    throw error;
  }

  try {
    const result = await createRepairReport({ repairItemId, repairReportNo });
    return { created: true, data: result?.data, message: result?.message };
  } catch (error) {
    if (isRepairReportNoDuplicated(error)) {
      try {
        await fetchSuggestedRepairReportNo();
      } catch {
        // 重取失敗仍提示手動再試
      }
      toast.info(
        '報告單號已存在，已重新取得建議單號，請再按一次「建立報告」（勿自動重送）',
      );
      return { noDuplicated: true };
    }

    if (isRepairReportAlreadyExists(error)) {
      const existingId = await resolveExistingRepairReportId({
        error,
        serialNo: line?.serialNo,
        repairItemId,
      });
      toast.info('此序號已有維修報告');
      return { alreadyExists: true, existingId };
    }

    toast.error(getRepairReportApiErrorMessage(error, '建立維修報告失敗'));
    throw error;
  }
}
